import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from '../utils/axios';

const NewsletterUnsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const statusParam = searchParams.get('status');
    const errorParam = searchParams.get('error');

    if (emailParam) {
      setEmail(emailParam);
    }

    const successParam = searchParams.get('success');

    if (statusParam === 'success' || successParam === '1') {
      setStatus('success');
    } else if (statusParam === 'already') {
      setStatus('already');
    } else if (errorParam === 'invalid_email' || errorParam === 'missing_email') {
      setStatus('invalid_email');
    } else if (errorParam === 'not_found') {
      setStatus('not_found');
    } else if (errorParam === 'failed') {
      setStatus('error');
    } else if (emailParam && !statusParam && !errorParam) {
      // Legacy behavior - try to unsubscribe via API
      handleUnsubscribe(emailParam);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const handleUnsubscribe = async (emailAddress) => {
    try {
      const response = await axios.post('/newsletter/unsubscribe', {
        email: emailAddress
      });

      if (response.data.success) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'success':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Successfully Unsubscribed</h1>
            <p className="text-gray-600 mb-6">
              You have been unsubscribed from our newsletter. We're sorry to see you go!
            </p>
            {email && (
              <p className="text-sm text-gray-500">
                Email: {email}
              </p>
            )}
          </div>
        );

      case 'already':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Already Unsubscribed</h1>
            <p className="text-gray-600 mb-6">
              You are already unsubscribed from our newsletter.
            </p>
            {email && (
              <p className="text-sm text-gray-500">
                Email: {email}
              </p>
            )}
          </div>
        );

      case 'invalid_email':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Email</h1>
            <p className="text-gray-600 mb-6">
              The email address in the unsubscribe link is invalid.
            </p>
          </div>
        );

      case 'not_found':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Not Found</h1>
            <p className="text-gray-600 mb-6">
              The email address is not found in our newsletter subscription list.
            </p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Unsubscribe Failed</h1>
            <p className="text-gray-600 mb-6">
              We couldn't process your unsubscribe request. The link may be invalid or expired.
            </p>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
              <svg className="animate-spin h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing...</h1>
            <p className="text-gray-600">
              Please wait while we process your unsubscribe request.
            </p>
          </div>
        );
    }
  };

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white shadow rounded-lg p-8">
            {renderContent()}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NewsletterUnsubscribe;
